import { useMemo, useState } from "react";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useTreatments } from "@/hooks/useTreatments";
import { useDoseEvents } from "@/hooks/useDoseEvents";
import { AppLayout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/Card";
import { StatTile } from "@/components/StatTile";
import { EmptyState } from "@/components/EmptyState";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Button } from "@/components/Button";
import { MedLevelChart, estimatedLevelAt } from "@/components/MedLevelChart";
import { LogDoseDialog } from "@/components/LogDoseDialog";
import { SyringeIcon } from "@/components/icons";
import type { DoseEventRow, InjectionSite } from "@/lib/database.types";

const SITE_LABEL: Record<InjectionSite, string> = {
  abdomen_left:  "Abdomen · Left",
  abdomen_right: "Abdomen · Right",
  thigh_left:    "Thigh · Left",
  thigh_right:   "Thigh · Right",
  arm_left:      "Arm · Left",
  arm_right:     "Arm · Right",
};

function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function relativeDays(ms: number): string {
  const days = Math.round((ms - Date.now()) / 86_400_000);
  if (days < 0) return `${Math.abs(days)} day${days === -1 ? "" : "s"} overdue`;
  if (days === 0) return "due today";
  if (days === 1) return "due tomorrow";
  return `in ${days} days`;
}

export default function Shots() {
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);
  const treatmentsQ = useTreatments(patientQ.data?.id);
  const dosesQ = useDoseEvents(patientQ.data?.id);
  const [logOpen, setLogOpen] = useState(false);

  if (patientQ.isLoading || dosesQ.isLoading) return <LoadingScreen />;

  const patient = patientQ.data ?? null;
  const treatments = treatmentsQ.data ?? [];
  const activeTreatment = treatments.find((t) => t.status === "active") ?? treatments[0] ?? null;
  const doses = dosesQ.data ?? [];
  const lastDose = doses[0] ?? null; // newest-first from the hook

  const estLevel = doses.length > 0 ? estimatedLevelAt(doses, Date.now()) : null;
  // Weekly cadence: next dose is one week after the last one.
  const nextDoseMs = lastDose ? new Date(lastDose.taken_at).getTime() + 7 * 86_400_000 : null;

  return (
    <AppLayout>
      <PageHeader
        title="Shots"
        subtitle="Log each dose, rotate injection sites, and watch your estimated medication level."
        right={
          <Button size="sm" onClick={() => setLogOpen(true)}>
            + Log dose
          </Button>
        }
      />

      {doses.length === 0 ? (
        <EmptyState
          icon={<SyringeIcon />}
          title="No doses logged yet"
          body="Log your first dose and we'll start tracking your estimated medication level, site rotation, and history."
          cta={<Button onClick={() => setLogOpen(true)}>Log your first dose</Button>}
        />
      ) : (
        <>
          {/* Next scheduled dose */}
          {nextDoseMs && (
            <Card
              tone={nextDoseMs <= Date.now() ? "accent" : "default"}
              className="mb-4"
              title="Next dose"
              action={
                <button
                  type="button"
                  onClick={() => setLogOpen(true)}
                  className="text-fh-green hover:text-fh-green-dark"
                >
                  Log it →
                </button>
              }
            >
              <div className="flex items-center gap-3">
                <div className="w-[46px] h-[46px] rounded-[12px] bg-white border border-fh-green-light/50 flex items-center justify-center shrink-0 text-fh-green-dark">
                  <SyringeIcon />
                </div>
                <div>
                  <div className="text-[15px] font-semibold text-fh-ink">
                    {lastDose!.medication} · {Number(lastDose!.dose_mg)} mg
                  </div>
                  <div className="text-[13px] text-fh-gray">
                    {fmtWhen(new Date(nextDoseMs).toISOString())} · {relativeDays(nextDoseMs)}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Stat tiles */}
          <div className="flex gap-3.5 mb-4 flex-wrap">
            <StatTile label="Doses taken" value={doses.length} />
            <StatTile
              label="Last dose"
              value={`${Number(lastDose!.dose_mg)} mg`}
              hint={fmtWhen(lastDose!.taken_at)}
            />
            <StatTile
              label="Est. level now"
              value={estLevel != null ? `${estLevel.toFixed(2)} mg` : "—"}
              hint="modeled from your doses"
            />
          </div>

          {/* PK chart */}
          <Card title="Estimated medication levels" className="mb-4">
            <MedLevelChart doses={doses} />
          </Card>

          {/* History */}
          <Card title="Dose history">
            <DoseHistory doses={doses} />
          </Card>
        </>
      )}

      {logOpen && (
        <LogDoseDialog
          open={logOpen}
          onClose={() => setLogOpen(false)}
          patientId={patient?.id}
          treatment={activeTreatment}
          lastDoseMg={lastDose ? Number(lastDose.dose_mg) : null}
        />
      )}
    </AppLayout>
  );
}

// Doses grouped by month, newest first — mirrors how patients think about
// their titration schedule ("what was I on in June?").
function DoseHistory({ doses }: { doses: DoseEventRow[] }) {
  const groups = useMemo(() => {
    const map = new Map<string, DoseEventRow[]>();
    for (const d of doses) {
      const dt = new Date(d.taken_at);
      const key = dt.toLocaleDateString(undefined, { month: "long", year: "numeric" });
      const arr = map.get(key) ?? [];
      arr.push(d);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [doses]);

  return (
    <div>
      {groups.map(([month, items]) => (
        <div key={month} className="mb-4 last:mb-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-fh-gray mb-2">
            {month}
          </div>
          <ul className="divide-y divide-fh-border">
            {items.map((d) => (
              <li key={d.id} className="py-3 flex items-start gap-3">
                <div className="w-9 h-9 rounded-[10px] bg-fh-sage flex items-center justify-center shrink-0 text-fh-green-dark">
                  <SyringeIcon />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-fh-ink">
                    {d.medication} · {Number(d.dose_mg)} mg
                  </div>
                  <div className="text-[12.5px] text-fh-gray">
                    {fmtWhen(d.taken_at)}
                    {d.injection_site && <> · {SITE_LABEL[d.injection_site]}</>}
                    {d.pain_level != null && <> · pain {d.pain_level}/5</>}
                  </div>
                  {d.notes && (
                    <p className="text-[12.5px] text-fh-gray mt-1 leading-snug">{d.notes}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
